// "Games in progress" — lists the caller's active (started-but-not-finished)
// games and links back into the room. Used by the apex landing page (all games)
// and, filtered to a single game, on each game's home screen. Each entry shows
// the opponent so you can tell the games apart.

import { useEffect, useMemo, useState } from "react";
import type {
  ActiveGame,
  StatsUser,
  UserLookupResponse,
} from "@couplesgames/core-types";
import { useAuth } from "../store/auth.js";
import { config } from "../lib/config.js";
import { gameNameForSlug, gameUrlForSlug } from "../lib/games.js";

export interface ActiveGamesState {
  games: ActiveGame[];
  loading: boolean;
}

export function useActiveGames(): ActiveGamesState {
  const { session, getAccessToken } = useAuth();
  const [games, setGames] = useState<ActiveGame[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!session) {
      setGames([]);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const token = await getAccessToken();
        if (cancelled) return;
        const res = await fetch(`${config.statsApi}/api/stats/active`, {
          headers: {
            accept: "application/json",
            ...(token ? { authorization: `Bearer ${token}` } : {}),
          },
        });
        if (!res.ok) {
          if (!cancelled) setGames([]);
          return;
        }
        const body = (await res.json()) as { games?: ActiveGame[] };
        if (!cancelled) setGames(body.games ?? []);
      } catch {
        if (!cancelled) setGames([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [session, getAccessToken]);

  return { games, loading };
}

function userLabel(sub: string, names: Record<string, StatsUser>): string {
  const u = names[sub];
  if (u?.name) return u.name;
  if (u?.email) return u.email;
  return "Player";
}

function userPicture(sub: string, names: Record<string, StatsUser>): string | undefined {
  return names[sub]?.picture;
}

async function fetchUserNames(
  subs: string[],
  getAccessToken: () => Promise<string | null>,
): Promise<Record<string, StatsUser>> {
  const unique = [...new Set(subs.filter(Boolean))];
  if (unique.length === 0) return {};
  try {
    const token = await getAccessToken();
    const res = await fetch(`${config.statsApi}/api/stats/users`, {
      method: "POST",
      headers: {
        accept: "application/json",
        "content-type": "application/json",
        ...(token ? { authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ subs: unique }),
    });
    if (!res.ok) return {};
    const body = (await res.json()) as UserLookupResponse;
    return body.users ?? {};
  } catch {
    return {};
  }
}

function OpponentAvatar({ src, name }: { src?: string; name: string }): JSX.Element {
  if (src) {
    return (
      <img
        src={src}
        alt={name}
        className="h-5 w-5 rounded-full border border-slate-700 object-cover"
      />
    );
  }
  return (
    <div className="h-5 w-5 rounded-full border border-slate-700 bg-slate-800 flex items-center justify-center text-[10px] text-slate-300">
      {name.charAt(0).toUpperCase()}
    </div>
  );
}

/** Lists in-progress games, optionally filtered to one game slug. */
export function ResumeGames({ gameSlug }: { gameSlug?: string }): JSX.Element | null {
  const { games, loading } = useActiveGames();
  const { getAccessToken } = useAuth();
  const [names, setNames] = useState<Record<string, StatsUser>>({});

  const visible = useMemo(
    () => (gameSlug ? games.filter((g) => g.gameSlug === gameSlug) : games),
    [games, gameSlug],
  );

  // Resolve opponent display names for the visible games.
  useEffect(() => {
    const subs = visible.map((g) => g.opponentUserId);
    if (subs.length === 0) {
      setNames({});
      return;
    }
    let cancelled = false;
    void fetchUserNames(subs, getAccessToken).then((n) => {
      if (!cancelled) setNames(n);
    });
    return () => {
      cancelled = true;
    };
  }, [visible, getAccessToken]);

  if (loading) return null;
  if (visible.length === 0) return null;

  return (
    <section className="space-y-3">
      <div className="space-y-1">
        <h2 className="text-xl font-semibold">Games in progress</h2>
        <p className="text-slate-400 text-sm">
          Jump back into a game you got disconnected from.
        </p>
      </div>
      <ul className="space-y-2">
        {visible.map((g) => {
          const url = gameUrlForSlug(g.gameSlug);
          if (!url) return null;
          const opponent = userLabel(g.opponentUserId, names);
          return (
            <li key={`${g.gameSlug}-${g.roomCode}`}>
              <a
                href={`${url}/room/${g.roomCode}`}
                className="flex items-center justify-between rounded-lg border border-slate-800 bg-slate-900/50 px-4 py-3 transition hover:border-sky-500/50"
              >
                <span className="flex items-center gap-2">
                  <span className="font-medium text-slate-100">
                    {gameNameForSlug(g.gameSlug)}
                  </span>
                  <span className="flex items-center gap-1.5 text-slate-400">
                    <span className="text-slate-500">vs</span>
                    <OpponentAvatar
                      src={userPicture(g.opponentUserId, names)}
                      name={opponent}
                    />
                    <span>{opponent}</span>
                  </span>
                </span>
                <span className="text-sm font-medium text-sky-400">Rejoin →</span>
              </a>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
